// src/services/monthlyPayments.service.ts
// VERSIÓN CORREGIDA CON ACTUALIZACIÓN DE DEUDA

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  runTransaction,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MonthlyPayment,
  MembershipStatus,
  MonthlySummary,
  MonthlyPaymentListItem,
  AutoGenerationResult,
  PaymentMethod
} from '../types/monthlyPayments.types';
import { MembershipAssignment } from '../types/member.types';
import { apiClient, isMySqlEnabled } from './apiClient';

const getCurrentMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getDueDate = (monthStr: string): string => {
  return `${monthStr}-15`;
};

const paymentExists = async (
  gymId: string,
  memberId: string,
  membershipId: string,
  month: string
): Promise<boolean> => {
  const paymentId = `${month}-${memberId}-${membershipId}`;
  const paymentRef = doc(db, `gyms/${gymId}/monthlyPayments`, paymentId);
  const paymentSnap = await getDoc(paymentRef);
  return paymentSnap.exists();
};

const isOverdue = (dueDate: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  return today > due;
};

export const generateMonthlyPayments = async (
  gymId: string
): Promise<AutoGenerationResult> => {
  if (isMySqlEnabled()) {
    try {
      await apiClient.post('/payments/generate-monthly', { gymId });
    } catch (e) {
      // safe fallback
    }
    return {
      success: true,
      paymentsGenerated: 0,
      errors: [],
      summary: {
        totalMembers: 0,
        totalMemberships: 0,
        totalAmount: 0,
        skipped: {
          suspended: 0,
          alreadyExists: 0
        }
      }
    };
  }

  const result: AutoGenerationResult = {

    success: false,
    paymentsGenerated: 0,
    errors: [],
    summary: {
      totalMembers: 0,
      totalMemberships: 0,
      totalAmount: 0,
      skipped: {
        suspended: 0,
        alreadyExists: 0
      }
    }
  };

  try {
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Evitar problemas de zona horaria
    const todayStr = today.toISOString().split('T')[0];



    const membersRef = collection(db, `gyms/${gymId}/members`);
    const membersSnap = await getDocs(membersRef);

    result.summary.totalMembers = membersSnap.size;

    for (const memberDoc of membersSnap.docs) {
      const memberId = memberDoc.id;
      const memberData = memberDoc.data();
      const memberName = `${memberData.firstName} ${memberData.lastName}`;

      if (memberData.status !== 'active') continue;

      const membershipsRef = collection(
        db,
        `gyms/${gymId}/members/${memberId}/memberships`
      );
      const membershipsSnap = await getDocs(membershipsRef);

      for (const membershipDoc of membershipsSnap.docs) {
        const membership = membershipDoc.data() as MembershipAssignment;
        const membershipId = membershipDoc.id;

        result.summary.totalMemberships++;

        if (membership.status === 'suspended' || membership.status === 'cancelled') {
          if (membership.status === 'suspended') {
            result.summary.skipped.suspended++;
          }
          continue;
        }

        if (membership.autoRenewal === false) {
          continue;
        }

        // Verificar si la membresía física está vencida (endDate <= hoy)
        let endDate: Date;
        try {
          if (!membership.endDate) {
            // Si no tiene fecha de vencimiento, forzarla a vencer hoy para generar pago
            endDate = new Date(today);
            endDate.setDate(endDate.getDate() - 1);
          } else {
            endDate = new Date(membership.endDate + 'T12:00:00');
          }
        } catch {
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() - 1);
        }

        if (endDate > today) {
          // Aún no vence, omitir
          continue;
        }

        // 1. Determinar el inicio del nuevo período (el endDate anterior)
        const oldEndDateStr = membership.endDate || todayStr;
        const newStartDateStr = oldEndDateStr;

        // 2. Calcular el fin del nuevo período (+30 días)
        const newEndDateObj = new Date(newStartDateStr + 'T12:00:00');
        newEndDateObj.setDate(newEndDateObj.getDate() + 30);
        const newEndDateStr = newEndDateObj.toISOString().split('T')[0];

        // 3. Verificar si ya existe un pago generado para esta fecha de inicio
        const exists = await paymentExists(
          gymId,
          memberId,
          membershipId,
          newStartDateStr
        );

        if (exists) {
          result.summary.skipped.alreadyExists++;
          continue;
        }

        // 4. Obtener costo
        let currentPrice = membership.cost || 0;
        if (currentPrice === 0) {
          // Fallback: buscar precio en memberships generales
          try {
            const membershipDefRef = collection(db, `gyms/${gymId}/memberships`);
            const membershipDefQuery = query(
              membershipDefRef,
              where('activityId', '==', membership.activityId)
            );
            const membershipDefSnap = await getDocs(membershipDefQuery);
            if (!membershipDefSnap.empty) {
              currentPrice = membershipDefSnap.docs[0].data().cost || 0;
            }
          } catch (e) {
            console.warn('No se pudo obtener precio fallback:', e);
          }
        }

        // 5. Crear el pago pendiente
        const payment: MonthlyPayment = {
          memberId,
          memberName,
          membershipId,
          activityId: membership.activityId,
          activityName: membership.activityName,
          month: newStartDateStr.substring(0, 7), // "YYYY-MM"
          dueDate: newStartDateStr, // Vence el día que inicia el periodo
          amount: currentPrice,
          status: 'pending',
          autoGenerated: true,
          generatedAt: Timestamp.now()
        };

        const paymentId = `${newStartDateStr}-${memberId}-${membershipId}`;
        const paymentRef = doc(db, `gyms/${gymId}/monthlyPayments`, paymentId);

        await setDoc(paymentRef, payment);

        // 6. Actualizar la membresía física en la subcolección
        const membershipRef = doc(db, `gyms/${gymId}/members/${memberId}/memberships`, membershipId);
        await updateDoc(membershipRef, {
          startDate: newStartDateStr,
          endDate: newEndDateStr,
          paymentStatus: 'pending',
          currentAttendances: 0,
          updatedAt: Timestamp.now()
        });

        // 7. Incrementar la deuda del socio
        const memberRef = doc(db, `gyms/${gymId}/members`, memberId);
        const memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          const currentMemberData = memberSnap.data();
          const currentDebt = currentMemberData.totalDebt || 0;
          const newDebt = currentDebt + currentPrice;

          await updateDoc(memberRef, {
            totalDebt: newDebt,
            hasDebt: newDebt > 0,
            updatedAt: Timestamp.now()
          });


        }

        result.paymentsGenerated++;
        result.summary.totalAmount += currentPrice;


      }
    }

    result.success = true;

    return result;

  } catch (error: any) {
    console.error('Error generando pagos:', error);
    result.errors.push(error.message);
    return result;
  }
};

/**
 * 📅 Calcula el mes objetivo de cobro basado en la fecha de inicio.
 * Si es después del día 15, se cobra el mes siguiente.
 */
export const calculateTargetMonth = (startDateStr: string): string => {
  const startDate = new Date(startDateStr + 'T12:00:00');
  const startDay = startDate.getDate();
  
  let targetDate = new Date(startDate);
  if (startDay > 15) {
    targetDate.setMonth(targetDate.getMonth() + 1);
  }
  
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * 🎯 OPTIMIZACIÓN: Genera el pago mensual para UN SOLO SOCIO y UNA SOLA MEMBRESÍA.
 * Evita recorrer toda la base de datos (O(1) vs O(N)).
 */
export const generateSingleMemberPayment = async (
  gymId: string,
  memberId: string,
  membershipId: string
): Promise<{ success: boolean; generated: boolean; error?: string }> => {
  try {
    // 1. Obtener la membresía específica
    const membershipRef = doc(db, `gyms/${gymId}/members/${memberId}/memberships`, membershipId);
    const membershipSnap = await getDoc(membershipRef);
    
    if (!membershipSnap.exists()) {
      throw new Error('Membresía no encontrada');
    }
    
    const membership = membershipSnap.data() as MembershipAssignment;
    
    // 2. Determinar la fecha de inicio del pago (el startDate de la membresía)
    const startDateStr = membership.startDate || new Date().toISOString().split('T')[0];
    
    // 3. Verificar si ya existe el pago
    const exists = await paymentExists(gymId, memberId, membershipId, startDateStr);
    if (exists) {
      return { success: true, generated: false };
    }
    
    // 4. Obtener el nombre del socio si no lo tenemos
    let memberName = membership.memberName;
    if (!memberName) {
      const memberSnap = await getDoc(doc(db, `gyms/${gymId}/members`, memberId));
      if (memberSnap.exists()) {
        const d = memberSnap.data();
        memberName = `${d.firstName} ${d.lastName}`;
      }
    }

    // 5. Configurar el pago
    const cost = membership.cost || 0;
    const payment: MonthlyPayment = {
      memberId,
      memberName: memberName || 'Socio Desconocido',
      membershipId,
      activityId: membership.activityId,
      activityName: membership.activityName,
      month: startDateStr.substring(0, 7),
      dueDate: startDateStr,
      amount: cost,
      status: 'pending',
      autoGenerated: true,
      generatedAt: Timestamp.now()
    };

    const paymentId = `${startDateStr}-${memberId}-${membershipId}`;
    const paymentRef = doc(db, `gyms/${gymId}/monthlyPayments`, paymentId);

    // 6. Guardar pago
    await setDoc(paymentRef, payment);

    // Actualizar deuda del socio
    const memberRef = doc(db, `gyms/${gymId}/members`, memberId);
    const memberSnap = await getDoc(memberRef);

    if (memberSnap.exists()) {
      const currentMemberData = memberSnap.data();
      const currentDebt = currentMemberData.totalDebt || 0;
      const newDebt = currentDebt + cost;

      await updateDoc(memberRef, {
        totalDebt: newDebt,
        hasDebt: newDebt > 0,
        updatedAt: Timestamp.now()
      });
      

    }

    return { success: true, generated: true };

  } catch (error: any) {
    console.error('❌ Error en generateSingleMemberPayment:', error);
    return { success: false, generated: false, error: error.message };
  }
};

export const getPendingPaymentsList = async (
  gymId: string,
  month?: string
): Promise<MonthlyPaymentListItem[]> => {
  try {
    const targetMonth = month || getCurrentMonth();

    // 1. Obtener todos los pagos pendientes del mes
    const paymentsRef = collection(db, `gyms/${gymId}/monthlyPayments`);
    const q = query(
      paymentsRef,
      where('month', '==', targetMonth),
      orderBy('memberName')
    );

    const paymentsSnap = await getDocs(q);

    // 2. Agrupar por socio y obtener su información
    const memberPayments: Map<string, MonthlyPaymentListItem> = new Map();
    const memberIds: Set<string> = new Set();

    paymentsSnap.forEach((doc) => {
      const payment = doc.data() as MonthlyPayment;

      if (payment.status === 'paid' || payment.status === 'cancelled') return;

      memberIds.add(payment.memberId);

      const overdue = isOverdue(payment.dueDate);
      const status = overdue ? 'overdue' : 'pending';

      if (!memberPayments.has(payment.memberId)) {
        memberPayments.set(payment.memberId, {
          memberId: payment.memberId,
          memberName: payment.memberName,
          totalPending: 0,
          activitiesPendingCount: 0,
          isOverdue: overdue,
          daysOverdue: 0,
          pendingActivities: []
        });
      }

      const item = memberPayments.get(payment.memberId)!;
      item.totalPending += payment.amount;
      item.activitiesPendingCount++;

      if (overdue) {
        const today = new Date();
        const due = new Date(payment.dueDate);
        const days = Math.floor(
          (today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)
        );
        item.daysOverdue = Math.max(item.daysOverdue, days);
      }

      item.pendingActivities.push({
        paymentId: doc.id,
        membershipId: payment.membershipId,
        activityName: payment.activityName,
        amount: payment.amount,
        dueDate: payment.dueDate,
        status
      });
    });

    // 3. Obtener información completa de cada socio (TELÉFONO, EMAIL, NOTIFICACIONES)
    const enrichedPayments: MonthlyPaymentListItem[] = [];

    // ✅ CORREGIDO: Convertir Set a Array antes de iterar
    const memberIdsArray = Array.from(memberIds);

    for (let i = 0; i < memberIdsArray.length; i++) {
      const memberId = memberIdsArray[i];
      const memberRef = doc(db, `gyms/${gymId}/members`, memberId);
      const memberSnap = await getDoc(memberRef);

      const paymentItem = memberPayments.get(memberId)!;

      if (memberSnap.exists()) {
        const memberData = memberSnap.data();

        // Agregar información del socio
        paymentItem.memberPhone = memberData.phone || null;
        paymentItem.memberEmail = memberData.email || null;

        // Agregar información de notificaciones
        paymentItem.lastNotifiedAt = memberData.lastDebtNotification || null;
        paymentItem.notificationCount = memberData.debtNotificationCount || 0;
      }

      enrichedPayments.push(paymentItem);
    }

    // 4. Ordenar: primero vencidos, luego por nombre
    return enrichedPayments.sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      return a.memberName.localeCompare(b.memberName);
    });

  } catch (error) {
    console.error('Error obteniendo lista de pagos:', error);
    throw error;
  }
};

export const getMonthlySummary = async (
  gymId: string,
  month?: string
): Promise<MonthlySummary> => {
  if (isMySqlEnabled()) {
    try {
      const summaryData = await apiClient.get<MonthlySummary>('/payments/summary', { gymId, ...(month ? { month } : {}) });
      return summaryData;
    } catch (e) {
      console.error('Error obteniendo resumen desde MySQL API:', e);
      const targetMonth = month || getCurrentMonth();
      const [year, monthNum] = targetMonth.split('-').map(Number);
      return {
        year,
        month: monthNum,
        totalMembers: 0,
        totalToCollect: 0,
        totalCollected: 0,
        totalPending: 0,
        membersWithDebt: 0,
        membersUpToDate: 0,
        activitiesBreakdown: {}
      };
    }
  }

  try {

    const targetMonth = month || getCurrentMonth();
    const [year, monthNum] = targetMonth.split('-').map(Number);

    const paymentsRef = collection(db, `gyms/${gymId}/monthlyPayments`);
    const q = query(paymentsRef, where('month', '==', targetMonth));
    const paymentsSnap = await getDocs(q);

    const summary: MonthlySummary = {
      year,
      month: monthNum,
      totalMembers: 0,
      totalToCollect: 0,
      totalCollected: 0,
      totalPending: 0,
      membersWithDebt: 0,
      membersUpToDate: 0,
      activitiesBreakdown: {}
    };

    const membersSet = new Set<string>();
    const membersWithDebtSet = new Set<string>();

    paymentsSnap.forEach((doc) => {
      const payment = doc.data() as MonthlyPayment;

      membersSet.add(payment.memberId);
      summary.totalToCollect += payment.amount;

      if (payment.status === 'paid') {
        summary.totalCollected += payment.amount;
      } else if (payment.status === 'pending' || payment.status === 'overdue') {
        summary.totalPending += payment.amount;
        membersWithDebtSet.add(payment.memberId);
      }
      // Si es 'cancelled', no se suma a nada


      if (!summary.activitiesBreakdown[payment.activityName]) {
        summary.activitiesBreakdown[payment.activityName] = {
          members: 0,
          totalCost: 0,
          collected: 0,
          pending: 0
        };
      }

      const breakdown = summary.activitiesBreakdown[payment.activityName];
      breakdown.members++;
      breakdown.totalCost += payment.amount;

      if (payment.status === 'paid') {
        breakdown.collected += payment.amount;
      } else {
        breakdown.pending += payment.amount;
      }
    });

    summary.totalMembers = membersSet.size;
    summary.membersWithDebt = membersWithDebtSet.size;
    summary.membersUpToDate = summary.totalMembers - summary.membersWithDebt;

    return summary;

  } catch (error) {
    console.error('Error obteniendo resumen mensual:', error);
    throw error;
  }
};

export const registerPayment = async (
  gymId: string,
  paymentId: string,
  paymentMethod: PaymentMethod,
  transactionId?: string,
  memberId?: string,
  amount?: number
): Promise<{ success: boolean; error?: string }> => {
  if (isMySqlEnabled()) {
    try {
      const cleanMembershipId = paymentId.replace(/^pending_/, '');
      await apiClient.post('/payments', {
        gymId,
        memberId,
        membershipId: cleanMembershipId,
        amount: amount || 0,
        paymentMethod: paymentMethod || 'EFECTIVO',
      });
      return { success: true };
    } catch (err: any) {
      console.error('❌ Error registrando pago en MySQL:', err);
      return { success: false, error: err.message || 'Error al registrar el pago' };
    }
  }

  try {
    await runTransaction(db, async (transaction) => {
      // 1️⃣ TODAS LAS LECTURAS PRIMERO
      const paymentRef = doc(db, `gyms/${gymId}/monthlyPayments`, paymentId);
      const paymentSnap = await transaction.get(paymentRef);

      if (!paymentSnap.exists()) {
        throw new Error('Pago no encontrado');
      }

      const payment = paymentSnap.data() as MonthlyPayment;

      if (payment.status === 'paid') {
        throw new Error('Este pago ya fue registrado');
      }

      // Leer datos del socio ANTES de escribir
      const memberRef = doc(db, `gyms/${gymId}/members`, payment.memberId);
      const memberSnap = await transaction.get(memberRef);

      if (!memberSnap.exists()) {
        throw new Error('Socio no encontrado');
      }

      const memberData = memberSnap.data();
      const currentDebt = memberData.totalDebt || 0;
      const newDebt = Math.max(0, currentDebt - payment.amount);

      // 2️⃣ TODAS LAS ESCRITURAS DESPUÉS
      transaction.update(paymentRef, {
        status: 'paid',
        paidAt: Timestamp.now(),
        paidDate: new Date().toISOString().split('T')[0],
        transactionId: transactionId || null
      });

      transaction.update(memberRef, {
        totalDebt: newDebt,
        hasDebt: newDebt > 0,
        updatedAt: Timestamp.now()
      });

      if (isMySqlEnabled()) {
        apiClient.put(`/members/${payment.memberId}`, { totalDebt: newDebt }).catch(err => {
          console.warn('Error actualizando deuda en MySQL:', err);
        });
      }
    });

    return { success: true };

  } catch (error: any) {
    console.error('❌ Error registrando pago:', error);
    return { success: false, error: error.message };
  }
};

export const createMembership = async (
  gymId: string,
  memberId: string,
  memberName: string,
  activityId: string,
  activityName: string,
  startDate: string
): Promise<{ success: boolean; membershipId?: string; error?: string }> => {
  try {
    const membershipData: Partial<MembershipAssignment> = {
      memberId,
      memberName,
      activityId,
      activityName,
      startDate,
      status: 'active',
      autoRenewal: true,
      createdAt: Timestamp.now()
    };

    const membershipsRef = collection(
      db,
      `gyms/${gymId}/members/${memberId}/memberships`
    );
    const membershipRef = doc(membershipsRef);
    await setDoc(membershipRef, membershipData);

    // Generar pago del mes actual si no existe (Optimizado)
    await generateSingleMemberPayment(gymId, memberId, membershipRef.id);

    return { success: true, membershipId: membershipRef.id };

  } catch (error: any) {
    console.error('Error creando membresía:', error);
    return { success: false, error: error.message };
  }
};

export const suspendMembership = async (
  gymId: string,
  memberId: string,
  membershipId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const membershipRef = doc(
      db,
      `gyms/${gymId}/members/${memberId}/memberships`,
      membershipId
    );

    await updateDoc(membershipRef, {
      status: 'suspended',
      autoRenewal: false,
      updatedAt: Timestamp.now()
    });

    return { success: true };

  } catch (error: any) {
    console.error('Error suspendiendo membresía:', error);
    return { success: false, error: error.message };
  }
};

export const reactivateMembership = async (
  gymId: string,
  memberId: string,
  membershipId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const membershipRef = doc(
      db,
      `gyms/${gymId}/members/${memberId}/memberships`,
      membershipId
    );

    await updateDoc(membershipRef, {
      status: 'active',
      autoRenewal: true,
      updatedAt: Timestamp.now()
    });

    return { success: true };

  } catch (error: any) {
    console.error('Error reactivando membresía:', error);
    return { success: false, error: error.message };
  }
};

export const cancelMonthlyPayment = async (
  gymId: string,
  paymentId: string,
  reason: string = 'Anulación administrativa'
): Promise<{ success: boolean; error?: string }> => {
  if (isMySqlEnabled()) {
    try {
      const cleanMembershipId = paymentId.replace(/^pending_/, '');
      await apiClient.post(`/memberships/${cleanMembershipId}/cancel`, {
        debtAction: 'cancel',
        cancelReason: reason,
      });
      return { success: true };
    } catch (err: any) {
      console.error('❌ Error anulando cuota en MySQL:', err);
      return { success: false, error: err.message || 'Error al anular la cuota' };
    }
  }

  try {
    const paymentRef = doc(db, `gyms/${gymId}/monthlyPayments`, paymentId);
    const paymentSnap = await getDoc(paymentRef);

    if (!paymentSnap.exists()) {
      throw new Error('Pago no encontrado');
    }

    const payment = paymentSnap.data() as MonthlyPayment;

    if (payment.status === 'paid') {
      throw new Error('No se puede anular un pago que ya ha sido cobrado');
    }

    if (payment.status === 'cancelled') {
      throw new Error('Este pago ya está anulado');
    }

    // Leer datos del socio para actualizar su deuda
    const memberRef = doc(db, `gyms/${gymId}/members`, payment.memberId);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      throw new Error('Socio no encontrado');
    }

    const memberData = memberSnap.data();
    const currentDebt = memberData.totalDebt || 0;
    // Restar el monto del saldo pendiente del socio
    const newDebt = Math.max(0, currentDebt - payment.amount);

    const batch = writeBatch(db);

    // Actualizar el pago
    batch.update(paymentRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now(),
      cancelledReason: reason
    });

    // Actualizar la deuda del socio
    batch.update(memberRef, {
      totalDebt: newDebt,
      hasDebt: newDebt > 0,
      updatedAt: Timestamp.now()
    });

    await batch.commit();

    if (isMySqlEnabled()) {
      try {
        await apiClient.put(`/members/${payment.memberId}`, { totalDebt: newDebt });
      } catch (err) {
        console.warn('Error actualizando deuda tras anulación en MySQL:', err);
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('❌ Error anulando pago:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Recalcula la deuda real de un socio sumando sus pagos pendientes/vencidos.
 * Útil para corregir inconsistencias en el campo totalDebt del documento del socio.
 */
export const recalculateMemberDebt = async (
  gymId: string,
  memberId: string
): Promise<{ success: boolean; newDebt: number; error?: string }> => {
  if (isMySqlEnabled()) {
    try {
      const response: any = await apiClient.get(`/members/${memberId}`);
      const member = response.member || response || {};
      const rawMemberships = member.memberships || [];
      let pendingDebt = 0;
      rawMemberships.forEach((m: any) => {
        if ((m.paymentStatus === 'PENDING' || m.paymentStatus === 'pending') && m.status !== 'CANCELLED' && m.status !== 'cancelled' && Number(m.cost || 0) > 0) {
          pendingDebt += Number(m.cost || 0);
        }
      });
      if (pendingDebt !== member.totalDebt) {
        apiClient.put(`/members/${memberId}`, { totalDebt: pendingDebt, hasDebt: pendingDebt > 0 }).catch(() => {});
      }
      return { success: true, newDebt: pendingDebt };
    } catch (err: any) {
      return { success: false, newDebt: 0, error: err.message };
    }
  }

  try {
    const paymentsRef = collection(db, `gyms/${gymId}/monthlyPayments`);
    const q = query(paymentsRef, where('memberId', '==', memberId));
    const snapshot = await getDocs(q);

    let realDebt = 0;

    snapshot.forEach(doc => {
      const payment = doc.data() as MonthlyPayment;
      if (payment.status === 'pending' || payment.status === 'overdue') {
        realDebt += payment.amount;
      }
    });

    try {
      const memberRef = doc(db, `gyms/${gymId}/members`, memberId);
      await updateDoc(memberRef, {
        totalDebt: realDebt,
        hasDebt: realDebt > 0,
        updatedAt: Timestamp.now()
      });
    } catch (e) {
      // firestore update fallback
    }

    return { success: true, newDebt: realDebt };
  } catch (error: any) {
    console.error('❌ Error recalculando deuda:', error);
    return { success: false, newDebt: 0, error: error.message };
  }
};

/**
 * 🧾 OBTENER HISTORIAL DE PAGOS DE UN SOCIO (Para el Portal de Socios)
 */
export const getMemberPayments = async (
  gymId: string,
  memberId: string,
  limitCount: number = 24
): Promise<MonthlyPayment[]> => {
  if (isMySqlEnabled()) {
    try {
      const response: any = await apiClient.get(`/members/${memberId}`);
      const member = response.member || response || {};
      const rawPayments = member.payments || [];
      const rawMemberships = member.memberships || [];

      const paidPayments: MonthlyPayment[] = rawPayments.map((p: any) => ({
        id: p.id,
        membershipId: p.membershipId || '',
        activityName: p.notes || p.concept || 'Pago de mensualidad',
        month: p.paymentDate ? String(p.paymentDate).slice(0, 7) : '',
        dueDate: p.paymentDate ? String(p.paymentDate).split('T')[0] : '',
        amount: Number(p.amount || 0),
        status: (p.status || 'paid').toLowerCase() as any,
        paidAt: p.paymentDate,
        paidDate: p.paymentDate ? String(p.paymentDate).split('T')[0] : '',
      }));

      const pendingPayments: MonthlyPayment[] = rawMemberships
        .filter((m: any) => (m.paymentStatus === 'PENDING' || m.paymentStatus === 'pending') && m.status !== 'CANCELLED' && m.status !== 'cancelled' && Number(m.cost || 0) > 0)
        .map((m: any) => {
          const startDateStr = m.startDate ? String(m.startDate).split('T')[0] : new Date().toISOString().split('T')[0];
          const endDateStr = m.endDate ? String(m.endDate).split('T')[0] : startDateStr;
          const todayStr = new Date().toISOString().split('T')[0];
          const isOverdue = endDateStr < todayStr;

          return {
            id: `pending_${m.id}`,
            membershipId: m.id,
            activityName: m.activity?.name || 'Membresía Pendiente',
            month: startDateStr.slice(0, 7),
            dueDate: startDateStr,
            amount: Number(m.cost || 0),
            status: isOverdue ? 'overdue' : 'pending',
          };
        });

      return [...pendingPayments, ...paidPayments];
    } catch (err) {
      console.error('Error fetching MySQL member payments:', err);
      return [];
    }
  }


  try {
    const paymentsRef = collection(db, `gyms/${gymId}/monthlyPayments`);

    const q = query(
      paymentsRef,
      where('memberId', '==', memberId),
      orderBy('month', 'desc')
    );

    const snapshot = await getDocs(q);
    const payments: MonthlyPayment[] = [];

    snapshot.forEach(doc => {
      payments.push({
        id: doc.id,
        ...doc.data()
      } as MonthlyPayment);
    });

    return payments;
  } catch (error) {
    console.error('Error getting member payments:', error);
    throw error;
  }
};

/**
 * 🔄 RENUEVA LA VIGENCIA Y ASISTENCIAS DE UNA MEMBRESÍA
 * Se llama automáticamente cuando se detecta un pago exitoso.
 */
export const renewMembershipOnPayment = async (
  gymId: string, 
  memberId: string, 
  membershipId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const membershipRef = doc(db, `gyms/${gymId}/members/${memberId}/memberships`, membershipId);
    const membershipSnap = await getDoc(membershipRef);

    if (!membershipSnap.exists()) {
      throw new Error('Membresía no encontrada');
    }

    const membershipData = membershipSnap.data();
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Evitar problemas de zona horaria

    // 1. Calcular nueva fecha de vencimiento
    // Si ya venció, empezamos desde hoy. Si no, extendemos desde el vencimiento actual.
    const currentEndDate = membershipData.endDate ? new Date(membershipData.endDate + 'T12:00:00') : today;
    const baseDate = currentEndDate < today ? today : currentEndDate;
    
    const newEndDate = new Date(baseDate);
    newEndDate.setDate(newEndDate.getDate() + 30);
    const newEndDateStr = newEndDate.toISOString().split('T')[0];

    // 2. Actualizar en Firestore
    await updateDoc(membershipRef, {
      endDate: newEndDateStr,
      currentAttendances: 0, // Resetea asistencias como pidió el usuario
      status: 'active',
      updatedAt: Timestamp.now()
    });

    // console.log(`✅ Membresía ${membershipId} renovada hasta ${newEndDateStr} para ${memberId}`);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error renovando membresía:', error);
    return { success: false, error: error.message };
  }
};

export default {
  generateMonthlyPayments,
  generateSingleMemberPayment,
  getPendingPaymentsList,
  getMonthlySummary,
  registerPayment,
  createMembership,
  suspendMembership,
  reactivateMembership,
  cancelMonthlyPayment,
  recalculateMemberDebt,
  renewMembershipOnPayment,
  getMemberPayments
};
